import { Columns3, RotateCcw, Trophy, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComparisonView, JobDetail } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Popover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState, SystemState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { formatDate } from '../../../lib/format';
import { registerCommands } from '../../../shell/command-registry';
import { useRoom } from '../../../shell/SocketProvider';
import { useComparison } from '../api/comparison-api';
import { useComparisonActions, type ComparisonActions } from '../hooks/use-comparison-actions';
import { usePins } from '../hooks/use-pins';
import { ComparisonTable } from './ComparisonTable';
import { HoldList } from './HoldList';
import styles from './Compare.module.css';

/**
 * DEC-02 — the screen where the hire is chosen (S22): finalists side by side, the final
 * selection, the hold list and the eliminated. A tab on the job, so N-55, N-65 and N-79
 * land on it. Everything it shows and allows comes from the server; the screen only
 * changes it at once and reconciles (§5a).
 */
export function CompareTab({ detail }: { readonly detail: JobDetail }): ReactNode {
  const query = useComparison(detail.id);
  const actions = useComparisonActions(detail.id);
  const { pins, toggle } = usePins(detail.id);
  const [adding, setAdding] = useState<AnchorRect | null>(null);
  const addRef = useRef<HTMLButtonElement | null>(null);
  // Another person's selection, hold or release reaches this screen as a hint on the job's room.
  useRoom(`job:${detail.id}`);

  const canAdd = query.data?.can.arrange === true && query.data.addable.length > 0;
  // 06-qol §1: the screen's own action comes first in the palette while it is open.
  useEffect(() => {
    if (!canAdd) return undefined;
    return registerCommands([
      {
        id: `comparison.add.${detail.id}`,
        label: 'Add someone to the comparison',
        section: 'action',
        context: detail.publicTitle ?? detail.title,
        permission: 'stage_decision',
        icon: UserPlus,
        keywords: ['compare', 'finalist', 'shortlist', 'side by side'],
        run: () => {
          if (addRef.current) setAdding(anchorFrom(addRef.current));
        },
      },
    ]);
  }, [canAdd, detail.id, detail.publicTitle, detail.title]);

  if (query.isPending) return <SkeletonLines count={10} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this job's comparison" contact="Your admin" />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  const view = query.data;

  return (
    <div className={styles.tab}>
      <header className={styles.summary}>
        <div className={styles.summaryText}>
          <h2 className={styles.heading}>Final selection</h2>
          <p className={styles.meta}>{placesWords(view)}</p>
        </div>
        {view.can.arrange && (
          <Button ref={addRef} variant="outline" icon={UserPlus} disabled={view.addable.length === 0} title={view.addable.length === 0 ? 'Everyone still interviewing is already here' : undefined} onClick={(event) => setAdding(anchorFrom(event.currentTarget))}>
            Add to compare
          </Button>
        )}
      </header>
      {view.can.closedReason && <p className={styles.notice}>{view.can.closedReason}</p>}

      {view.columns.length === 0 ? (
        <SystemState
          icon={Trophy}
          title="Nobody has passed the final round yet"
          body="When the hiring manager passes someone on the last round's debrief, they appear here side by side. You can also add anyone still interviewing to compare them."
          isInset
        />
      ) : (
        <ComparisonTable view={view} pins={pins} onTogglePin={toggle} actions={actions} />
      )}

      <HoldList view={view} actions={actions} />
      <Eliminated view={view} actions={actions} />

      <Popover anchor={adding} onClose={() => setAdding(null)} align="right" label="Add to compare">
        <AddList
          view={view}
          onAdd={(applicationId, name) => {
            actions.add(applicationId, name);
            setAdding(null);
          }}
        />
      </Popover>
    </div>
  );
}

function placesWords(view: ComparisonView): string {
  const { total, selected, left } = view.places;
  const places = total === 1 ? '1 place' : `${total} places`;
  if (left === 0) return `${places} · all chosen`;
  return `${places} · ${selected} chosen · ${left} still to choose`;
}

function AddList({ view, onAdd }: { readonly view: ComparisonView; readonly onAdd: (applicationId: string, name: string) => void }): ReactNode {
  return (
    <div className={styles.menu}>
      <p className={styles.menuTitle}>
        Add to compare
        <span className={styles.menuHint}>To compare only — only a finalist can be selected.</span>
      </p>
      <ul className={styles.addList}>
        {view.addable.map((row) => (
          <li key={row.applicationId}>
            <button type="button" className={styles.menuItem} onClick={() => onAdd(row.applicationId, row.candidateName)}>
              {row.candidateName}
              <span className={styles.menuHint}>{row.stageLabel}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Eliminated({ view, actions }: { readonly view: ComparisonView; readonly actions: ComparisonActions }): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  if (view.eliminated.length === 0) return null;
  return (
    <Card
      title={`Eliminated (${view.eliminated.length})`}
      meta="Off the comparison, not rejected — nothing was sent to them."
      action={
        <Button variant="ghost" size="sm" icon={Columns3} onClick={() => setIsOpen((current) => !current)}>
          {isOpen ? 'Hide' : 'Show'}
        </Button>
      }
    >
      {isOpen && (
        <ul className={styles.holds}>
          {view.eliminated.map((row) => (
            <li key={row.applicationId} className={styles.hold}>
              <div className={styles.holdWho}>
                <span className={styles.name}>{row.candidateName}</span>
                <span className={styles.quiet}>
                  {row.reasonLabel}
                  {row.note ? ` — “${row.note}”` : ''} · {row.byName ?? 'Someone'}, {formatDate(row.at)}
                </span>
              </div>
              {view.can.arrange && (
                <Button variant="ghost" size="sm" icon={RotateCcw} isPending={actions.pendingOf(row.applicationId) === 'restore'} onClick={() => actions.restore(row.applicationId, row.candidateName)}>
                  Restore
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
